/**
 * ARIA voice utilities - voice selection, speak, listen, command parser.
 * Speech pipeline:
 *   1. Try ElevenLabs (Rachel M British female) via backend proxy → audio/mpeg blob
 *   2. On any failure (network, 4xx, 5xx, audio decode), fall back to Web Speech API SpeechSynthesis
 * The current voice engine ("elevenlabs" | "fallback" | "unknown") is observable
 * via getVoiceEngine() / subscribeVoiceEngine() so the ARIA UI can show a status dot.
 *
 * iOS Safari: speak() must be called within the user-gesture task. The first
 * call after a gesture unlocks the Audio element for subsequent programmatic plays.
 */

import { API } from "@/lib/api";

// Preferred British female voice names across operating systems.
const VOICE_PREFS = [
  "Kate (Premium)", "Kate", "Serena (Premium)", "Serena", "Stephanie", "Catherine", "Fiona",
  "Microsoft Sonia Online (Natural) - English (United Kingdom)",
  "Microsoft Libby Online (Natural) - English (United Kingdom)",
  "Microsoft Hazel Desktop - English (Great Britain)",
  "Microsoft Susan Desktop - English (Great Britain)",
  "Microsoft Sonia", "Microsoft Libby", "Microsoft Hazel", "Microsoft Susan",
  "Google UK English Female",
  "Karen",
];

const KNOWN_MALE_VOICES = [
  "Daniel", "Arthur", "Oliver", "George", "Gordon", "Aaron", "Albert", "Bruce",
  "Fred", "Ralph", "Rishi", "Lee", "Microsoft George",
  "Microsoft Ryan", "Mark", "Tom", "James",
];

function isLikelyMale(voiceName) {
  if (!voiceName) return false;
  const n = voiceName.toLowerCase();
  if (/\bmale\b/.test(n) && !/\bfemale\b/.test(n)) return true;
  return KNOWN_MALE_VOICES.some((m) => voiceName.includes(m));
}

const STORAGE_KEY = "aria_settings";

export function getSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaults(), ...JSON.parse(raw) };
  } catch {
    // ignore
  }
  return defaults();
}

function defaults() {
  return {
    enabled: true,
    accent: "british",
    rate: 0.95,
    pitch: 1.05,
    proactive: true,
    seenModules: {},
  };
}

export function saveSettings(s) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function selectVoice(accent = "british") {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;

  if (accent !== "australian") {
    for (const pref of VOICE_PREFS) {
      const v = voices.find((vv) => vv.name === pref || vv.name.includes(pref));
      if (v && !isLikelyMale(v.name)) return v;
    }
    const enGB = voices.filter((v) => (v.lang === "en-GB" || v.lang === "en_GB"));
    const enGBFem = enGB.find((v) => !isLikelyMale(v.name));
    if (enGBFem) return enGBFem;
  }

  const enAU = voices.find((v) => (v.lang === "en-AU" || v.lang === "en_AU") && !isLikelyMale(v.name));
  if (enAU) return enAU;

  const englishFem = voices.find((v) => v.lang.toLowerCase().startsWith("en") && !isLikelyMale(v.name));
  return englishFem || voices[0];
}

// --- Voice engine state (observable so the ARIA dot can react) -------------
let _voiceEngine = "unknown"; // "elevenlabs" | "fallback" | "unknown"
const _engineListeners = new Set();
function _setVoiceEngine(next) {
  if (_voiceEngine === next) return;
  _voiceEngine = next;
  _engineListeners.forEach((cb) => { try { cb(_voiceEngine); } catch { /* ignore */ } });
}
export function getVoiceEngine() { return _voiceEngine; }
export function subscribeVoiceEngine(cb) {
  _engineListeners.add(cb);
  cb(_voiceEngine);
  return () => _engineListeners.delete(cb);
}

// --- Shared HTMLAudioElement (iOS Safari gesture-unlock pattern) -----------
// Created once and reused. The first .play() call inside a user-gesture task
// unlocks the element so subsequent programmatic plays work on iOS Safari.
let _audioEl = null;
function _getAudioEl() {
  if (_audioEl) return _audioEl;
  _audioEl = new Audio();
  _audioEl.preload = "auto";
  return _audioEl;
}

let _currentUtterance = null;
let _currentAudioUrl = null;
let _currentEndCb = null;

function _cleanupAudio() {
  if (_currentAudioUrl) {
    try { URL.revokeObjectURL(_currentAudioUrl); } catch { /* ignore */ }
    _currentAudioUrl = null;
  }
}

// --- Fallback Web-Speech-API path -----------------------------------------
function _speakViaSpeechSynthesis(text, opts) {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    opts.onEnd?.();
    return;
  }
  const s = getSettings();
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  const accentToUse = opts.voiceOverride || s.accent;
  const v = selectVoice(accentToUse);
  if (v) u.voice = v;
  u.rate = opts.rate ?? s.rate;
  u.pitch = opts.pitch ?? s.pitch;
  u.lang = v?.lang || (accentToUse === "australian" ? "en-AU" : "en-GB");
  u.onstart = () => { _currentUtterance = u; opts.onStart?.(); };
  u.onend = () => { _currentUtterance = null; opts.onEnd?.(); };
  u.onerror = () => { _currentUtterance = null; opts.onEnd?.(); };
  _setVoiceEngine("fallback");
  window.speechSynthesis.speak(u);
}

// --- Primary ElevenLabs path ----------------------------------------------
async function _speakViaElevenLabs(text, opts) {
  const token = localStorage.getItem("medlogix_token");
  if (!token) throw new Error("not_authenticated");
  // Pre-create the audio element synchronously inside the calling user-gesture
  // task so iOS Safari permits the subsequent programmatic play().
  const audio = _getAudioEl();

  const resp = await fetch(`${API}/aria/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ text }),
  });
  if (!resp.ok) throw new Error(`tts_${resp.status}`);
  const blob = await resp.blob();
  if (blob.size === 0) throw new Error("tts_empty");

  _cleanupAudio();
  _currentAudioUrl = URL.createObjectURL(blob);
  audio.src = _currentAudioUrl;

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onErr);
    };
    const onEnd = () => { cleanup(); opts.onEnd?.(); _cleanupAudio(); resolve(); };
    const onErr = () => { cleanup(); reject(new Error("audio_decode_error")); };
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onErr);
    opts.onStart?.();
    _setVoiceEngine("elevenlabs");
    audio.play().catch((e) => { cleanup(); reject(e); });
  });
}

export function speak(text, opts = {}) {
  if (typeof window === "undefined") return;
  const s = getSettings();
  if (!s.enabled) return;
  cancel();
  _currentEndCb = opts.onEnd;
  // Try ElevenLabs first; on ANY failure fall back to SpeechSynthesis seamlessly.
  _speakViaElevenLabs(text, opts).catch(() => {
    _speakViaSpeechSynthesis(text, opts);
  });
}

export function cancel() {
  if (typeof window === "undefined") return;
  if (window.speechSynthesis) window.speechSynthesis.cancel();
  if (_audioEl) {
    try { _audioEl.pause(); _audioEl.currentTime = 0; } catch { /* ignore */ }
  }
  _cleanupAudio();
  _currentUtterance = null;
  _currentEndCb?.();
  _currentEndCb = null;
}

export function isSpeaking() {
  if (_audioEl && !_audioEl.paused && !_audioEl.ended) return true;
  return _currentUtterance != null || (typeof window !== "undefined" && window.speechSynthesis?.speaking);
}

// --- Commands ---
export const ARIA_COMMANDS = [
  { phrases: ["open charting", "new note", "start chart"], path: "/charting", say: "Opening AI Charting." },
  { phrases: ["show prior auths", "open prior auth", "prior authorization"], path: "/prior-auth", say: "Here are your prior authorizations." },
  { phrases: ["open referrals", "show referrals", "new referral"], path: "/referrals", say: "Opening Referrals." },
  { phrases: ["show revenue cycle", "revenue cycle", "show claims"], path: "/revenue-cycle", say: "Here is your Revenue Cycle Command." },
  { phrases: ["show denials", "denied claims"], path: "/revenue-cycle", say: "Showing denial management." },
  { phrases: ["open pharmacy", "pharmacy intelligence", "show medications"], path: "/pharma-manager", say: "Opening Pharmacy Intelligence." },
  { phrases: ["show analytics", "open analytics", "dashboard"], path: "/analytics", say: "Here is the analytics dashboard." },
  { phrases: ["open voice engine", "voice intelligence", "ambient capture"], path: "/voice-engine", say: "Opening Voice Intelligence Engine." },
  { phrases: ["new encounter", "start encounter"], path: "/charting", say: "Starting a new encounter." },
  { phrases: ["show my patients", "open patients", "patient list"], path: "/patient-engagement", say: "Here are your patients." },
  { phrases: ["open dashboard", "command center", "home"], path: "/dashboard", say: "Returning to the Command Center." },
  { phrases: ["open security", "security admin"], path: "/security-admin", say: "Opening Security Admin." },
  { phrases: ["open risk stratification", "risk panel"], path: "/risk-stratification", say: "Opening Risk Stratification." },
  { phrases: ["open predictive", "predictive ai"], path: "/predictive", say: "Opening Predictive AI." },
  { phrases: ["open emergency", "emergency protocols"], path: "/emergency-protocols", say: "Opening Emergency Protocols." },
  { phrases: ["open legal", "legal intelligence"], path: "/legal-intelligence", say: "Opening Legal Intelligence." },
  { phrases: ["open contracts", "contract intelligence"], path: "/contract-intelligence", say: "Opening Contract Intelligence." },
  { phrases: ["open cme", "cme tracker", "licensing"], path: "/cme-tracker", say: "Opening CME and Licensing Tracker." },
  { phrases: ["open financial wellness", "show finances"], path: "/financial-wellness", say: "Opening Financial Wellness." },
];

export function parseCommand(text) {
  if (!text) return null;
  const t = text.toLowerCase().trim();
  for (const cmd of ARIA_COMMANDS) {
    for (const p of cmd.phrases) {
      if (t.includes(p)) return cmd;
    }
  }
  if (/what needs attention|what do i need|action items|pending/.test(t)) {
    return { type: "attention" };
  }
  if (/help|guide|tour|how do i/.test(t)) {
    return { type: "help" };
  }
  if (/thank you|thanks|bye|goodbye/.test(t)) {
    return { type: "thanks", say: "You're most welcome. I'm always here." };
  }
  return null;
}

// --- Module greetings (first-visit intros) ---
export const MODULE_GREETINGS = {
  "/charting": "Welcome to AI Charting. Dictate or type your encounter, and I'll structure a SOAP note with CPT and ICD-10 codes in seconds.",
  "/prior-auth": "Welcome to Prior Authorization AI. I can submit a complete prior authorization in under 60 seconds. Just tell me the patient name and procedure and I'll handle everything else.",
  "/revenue-cycle": "This is your Revenue Cycle Command Center. I monitor every claim from submission to payment and flag anything that needs your attention.",
  "/pharma-manager": "Welcome to Pharmacy Intelligence. I check every medication for interactions, dosing issues, and formulary coverage before you prescribe.",
  "/referrals": "Welcome to the Specialist Referral Hub. Tell me the diagnosis and specialty, and I'll generate the full referral packet from the patient's encounter history.",
  "/liability": "This is your Liability Shield. I score every signed encounter for documentation completeness and flag anything under seventy percent for review.",
  "/staffing": "Welcome to Staffing. I track credentials, surface anything expiring within ninety days, and help you assign tasks.",
  "/voice-engine": "Voice Intelligence Engine. Tap the microphone and dictate freely. I'll extract referrals, prior auths, medications, codes, and risk flags from anything you say.",
  "/predictive": "Welcome to Predictive Clinical Intelligence. I forecast revenue, predict claim denials, and identify patients at risk of deterioration in the next 72 hours.",
  "/risk-stratification": "Risk Stratification. I sort your entire panel into low, medium, and high risk so you can focus your time where it matters most.",
  "/emergency-protocols": "Emergency Protocols. Tap any protocol for step-by-step guidance during a critical situation.",
  "/legal-intelligence": "Legal Intelligence Layer. I scan every clinical note for malpractice exposure before you sign, and draft demand-letter responses if needed.",
  "/contract-intelligence": "Contract Intelligence. Upload any payer contract and I'll identify revenue leakage and suggest renegotiation arguments.",
  "/financial-wellness": "Financial Wellness. I track your practice revenue versus personal income, retirement growth, tax opportunities, and partnership equity.",
  "/cme-tracker": "CME and Licensing Tracker. I monitor your credit progress and every license, certification, and registration expiration.",
  "/security-admin": "Security Command Center. Real-time login map, WAF blocks, locked accounts, recovery queue, and two-factor compliance — all here.",
  "/analytics": "Analytics Dashboard. KPIs, revenue trends, denial intelligence, and burnout indicators across your providers.",
  "/managed-care": "Managed Care Intelligence. HEDIS gaps, STARS ratings, HCC coding opportunities, and contract performance by payer.",
  "/care-coordination": "Care Coordination. Discharge planning, readmission risk, and the patients overdue for follow-up.",
  "/hospital-ops": "Hospital Operations. OR schedule, bed management, physician credentialing, and equipment readiness.",
  "/patient-engagement": "Patient Engagement Hub. Send appointment reminders in bulk, generate post-visit instructions, and track no-show patterns.",
  "/pa-np-hub": "PA and NP Collaboration Hub. Supervision agreements, co-signature queue, scope-of-practice flags, and performance tracking.",
  "/peer-consult": "Peer Consult Network. Submit anonymous cases for second opinion — I'll match them to the right specialist by complexity.",
  "/telehealth": "Telehealth Intelligence. Visit quality scoring, state prescribing rules, and payer-specific virtual reimbursement.",
  "/data-intelligence": "Data Ownership. Export complete records in any format, match patients to clinical trials, and review monetization options.",
  "/reputation-manager": "Reputation Manager. Multi-platform reviews with HIPAA-safe AI-drafted responses.",
  "/ehr-bridge": "EHR Bridge. Unified patient timeline across encounters, labs, referrals, and claims.",
  "/dashboard": "Welcome back to the Command Center. Everything important is here.",
  "/bridge": "Welcome to the CamelionAI Bridge. Eight unified connections across ZidaMD, ProsthoAI, and ReimbursePro.",
  "/eod": "End of Day Mode. I'll help you clear your queue in fifteen minutes.",
  "/wellbeing": "Your wellbeing dashboard. Private to you. Never shared.",
  "/documentation-pro": "Documentation Pro. One sentence in, complete SOAP note out.",
};
